import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Trophy, Play } from 'lucide-react';
import { startMic } from '../../lib/samuel/mic';
import { decodeTo44k } from '../../lib/samuel/model';
import { analyzeClip, compareFormants, meanFormants } from '../../lib/samuel/analysis';

const Curves = ({ a, b }) => {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const W = cv.width = 600, H = cv.height = 160, g = cv.getContext('2d');
    g.fillStyle = '#020617'; g.fillRect(0, 0, W, H);
    const draw = (fs, col, k, max) => {
      const v = fs.map((f) => f && f[k]); g.fillStyle = col;
      v.forEach((x, i) => x && g.fillRect((i / Math.max(1, v.length - 1)) * W, H - (x / max) * H, 3, 3));
    };
    if (a) { draw(a.formants, '#22d3ee', 'f2', 3000); draw(a.formants, '#22d3ee88', 'f1', 3000); }
    if (b) { draw(b.formants, '#fbbf24', 'f2', 3000); draw(b.formants, '#fbbf2488', 'f1', 3000); }
  }, [a, b]);
  return <canvas ref={ref} className="w-full h-24 rounded-lg" data-testid="compare-curves" />;
};

export const CompareSteve = ({ lab, steveClips }) => {
  const [refIdx, setRefIdx] = useState(0);
  const [refA, setRefA] = useState(null);
  const [mine, setMine] = useState(null);
  const [rec, setRec] = useState(false);
  const micRef = useRef(null);
  const ref = steveClips[refIdx];
  useEffect(() => () => micRef.current?.stop(), []);
  useEffect(() => {
    setRefA(null); setMine(null);
    if (!ref) return;
    fetch(ref.url).then((r) => r.blob()).then(decodeTo44k).then((wav) => setRefA({ wav, ...analyzeClip(wav) })).catch(() => setRefA(null));
  }, [ref]);

  const toggle = async () => {
    if (rec) { micRef.current?.stop(); micRef.current = null; setRec(false); return; }
    try {
      micRef.current = await startMic({ vad: true, maxSec: 4, onUtterance: (wav) => {
        micRef.current?.stop(); micRef.current = null; setRec(false);
        setMine({ wav, ...analyzeClip(wav) });
      } });
      setRec(true);
    } catch { lab.setError('Microfono non disponibile'); }
  };
  const score = refA && mine ? compareFormants(refA.formants, mine.formants) : null;
  const mA = refA && meanFormants(refA.formants), mB = mine && meanFormants(mine.formants);

  if (!steveClips.length) return <p className="text-xs text-slate-500">Nessuna registrazione di Steve disponibile per il confronto.</p>;
  return (
    <div className="space-y-3" data-testid="compare-steve">
      <div className="flex flex-wrap gap-1.5">
        {steveClips.map((c, i) => <button key={c.url} onClick={() => setRefIdx(i)} data-testid={`compare-ref-${i}`} className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${i === refIdx ? 'bg-cyan-400 text-slate-950' : 'bg-slate-800 text-cyan-100'}`}>{c.label}</button>)}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => new Audio(ref.url).play()} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 text-cyan-100 text-sm" data-testid="compare-play-ref"><Play className="w-4 h-4" /> Steve</button>
        <button onClick={toggle} disabled={!refA} data-testid="compare-record" className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold disabled:opacity-40 ${rec ? 'bg-rose-500 text-white' : 'bg-amber-400 text-slate-950'}`}>
          {rec ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />} {rec ? 'Sto ascoltando… parla ora' : 'Ripeti tu'}
        </button>
        {mine && <button onClick={() => lab.addClip({ wav: mine.wav, kind: 'mic', label: `Tu · ${ref.label}` })} className="px-3 py-2 rounded-xl bg-slate-800 text-cyan-100 text-sm" data-testid="compare-imitate">Fai imitare a Samuel</button>}
        {score !== null && (
          <span className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold ${score >= 75 ? 'bg-emerald-500 text-slate-950' : score >= 50 ? 'bg-amber-400 text-slate-950' : 'bg-rose-500 text-white'}`} data-testid="compare-score">
            <Trophy className="w-4 h-4" /> {score}/100
          </span>
        )}
      </div>
      <Curves a={refA} b={mine} />
      <p className="text-[11px] text-slate-400"><span className="text-cyan-300">●</span> Steve · <span className="text-amber-300">●</span> tu — F2 (sopra) e F1 (sotto) nel tempo.
        {mA && mB && ` Media: F1 ${Math.round(mB.f1)} vs ${Math.round(mA.f1)} Hz · F2 ${Math.round(mB.f2)} vs ${Math.round(mA.f2)} Hz.`}</p>
    </div>
  );
};
